import { useCallback } from 'react';
import { message } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateSelectedMaterial } from '../../redux/slices/productsSlice';
import { setCurProductSelectedMaterial, setCurProductSelectedSize } from '../../redux/slices/curProductSlice';
import { clearSizeError, updateCartItemMaterialLocal } from '../../redux/slices/shoppingCartSlice';
import type {UseMaterialSelectionArgs, MaterialSelectorOption } from '../interfaces';
import { getNextCartMaterialSize, validateCartMaterialSelection } from './materialSelector.cart';
import { getProductSizeValues } from '../../utils/sizeOptions';

export const useMaterialSelection = ({
  selectedMaterial,
  cartItem,
  isCartPage,
  isProductDetailPage,
  productId,
  userId,
  onSelect,
}: UseMaterialSelectionArgs) => {
  const dispatch = useAppDispatch();
  const curProduct = useAppSelector((state) => state.curProduct.productId);
  const selectedSize = useAppSelector((state) => state.curProduct.selectedSize);

  return useCallback((material: MaterialSelectorOption) => {
    const materialName = material.materialName;
    if (materialName === selectedMaterial) return;

    const currentCartSize = cartItem?.selectedSize || '';
    const nextCartSize = isCartPage && cartItem ? getNextCartMaterialSize(material, cartItem) : currentCartSize;

    if (isCartPage && cartItem && !validateCartMaterialSelection(material, cartItem, nextCartSize)) {
      return;
    }

    if (onSelect) {
      onSelect(materialName);
      return;
    }

    if (isCartPage && cartItem) {
      if (nextCartSize && nextCartSize !== currentCartSize) {
        message.info(`Size changed to ${nextCartSize} for ${materialName}.`);
        dispatch(clearSizeError(cartItem.id));
      }

      dispatch(updateCartItemMaterialLocal({
        itemId: cartItem.id,
        selectedMaterial: materialName,
        selectedSize: nextCartSize,
        persistGuest: !userId,
      }));
    } else if (isProductDetailPage) {
      const productSizeOptions = curProduct
        ? getProductSizeValues(curProduct.sizes, curProduct.materialPrices, materialName)
        : [];
      const nextProductSize = selectedSize && productSizeOptions.includes(selectedSize)
        ? selectedSize
        : productSizeOptions[0] || '';
      dispatch(setCurProductSelectedMaterial(materialName));
      dispatch(setCurProductSelectedSize(nextProductSize));
    } else if (productId) {
      dispatch(updateSelectedMaterial({ productId, materialName }));
    }
  }, [cartItem, curProduct, dispatch, isCartPage, isProductDetailPage, onSelect, productId, selectedMaterial, selectedSize, userId]);
};
